import { lazy } from 'react';
import { Navigate, Routes as ReactRouterDomRoutes, Route } from 'react-router-dom';

import { useGetFeatureTogglesQuery } from '@/features/feature-toggle';
import { RobinJrChatLazy } from '@/pages/RobinJrChat';
import { RobinJrHomeLazy } from '@/pages/RobinJrHome';
import { useGetTenantBrandingQuery } from '@/shared/legacy/hooks/useGetTenantBrandingQuery';
import { RobinJrLayout } from '@/shared/ui/robin-jr-layout';
import { routes, shouldShowRobinWrapped } from 'config';
import { useRefreshToken } from 'hooks/shared';
import { useBrowserExtensionSync } from 'hooks/shared/useBrowserExtensionSync';
import { SharedLayoutComponents } from 'shared/legacy/components/layouts/SharedLayoutComponents/SharedLayoutComponents';
import { RJCommHubJobAlertsConfirmation } from 'views/LoggedOut/RJCommHubJobAlertsConfirmation/RJCommHubJobAlertsConfirmation';
import { RJCommHubPublicVacancyDetails } from 'views/LoggedOut/RJCommHubPublicVacancyDetails/RJCommHubPublicVacancyDetails';
import { RJCommHubQuestionnaireConfirmation } from 'views/LoggedOut/RJCommHubQuestionnaireConfirmation/RJCommHubQuestionnaireConfirmation';
import { RoutesSuspense } from './RoutesSuspense';
import { JamParams } from './components/JamParams/JamParams';
import RequireAuth from './components/RequireAuth/RequireAuth';
import { RobinJrRouteGuard } from './components/RobinJrRouteGuard';
import { useRedirectWhenTranslateOn } from './useRedirectWhenTranslateOn';

const LogIn = lazy(() => import('views/LoggedOut').then((module) => ({ default: module.LogIn })));
const LoginTenant = lazy(() => import('views/LoggedOut').then((module) => ({ default: module.LoginTenant })));
const MultiFactorAuth = lazy(() => import('views/LoggedOut').then((module) => ({ default: module.MultiFactorAuth })));
const PasswordRecovery = lazy(() => import('views/LoggedOut').then((module) => ({ default: module.PasswordRecovery })));
const ResetPassword = lazy(() => import('views/LoggedOut').then((module) => ({ default: module.ResetPassword })));
const RJCommHubCandidateInterested = lazy(
  () => import('views/LoggedOut/RJCommHubCandidateInterested/RJCommHubCandidateInterested'),
);
const JobRecommendationsPage = lazy(() =>
  import('@/pages/JobRecommendationsPage').then((m) => ({ default: m.JobRecommendationsPage })),
);
const JobRecommendationDetailsPage = lazy(() =>
  import('@/pages/JobRecommendationDetailsPage').then((m) => ({ default: m.JobRecommendationDetailsPage })),
);
const JobNotificationsEnabledPage = lazy(() =>
  import('@/pages/JobNotificationsEnabledPage').then((m) => ({ default: m.JobNotificationsEnabledPage })),
);
const JobRecommendationsPausedPage = lazy(() =>
  import('@/pages/JobRecommendationsPausedPage').then((m) => ({ default: m.JobRecommendationsPausedPage })),
);
const JobRecommendationNotificationDetailPage = lazy(() =>
  import('@/pages/JobRecommendationNotificationDetailPage').then((m) => ({
    default: m.JobRecommendationNotificationDetailPage,
  })),
);
const RJCommHubCandidateConfirmation = lazy(
  () => import('views/LoggedOut/RJCommHubCandidateConfirmation/RJCommHubCandidateConfirmation'),
);
const OptOutCommunication = lazy(() => import('views/LoggedOut/OptOutCommunication/OptOutCommunication'));
const InterviewFeedbackCandidatePage = lazy(() =>
  import('@/pages/InterviewFeedbackPage').then((m) => ({ default: m.InterviewFeedbackCandidatePage })),
);
const InterviewFeedbackRecruiterPage = lazy(() =>
  import('@/pages/InterviewFeedbackPage').then((m) => ({ default: m.InterviewFeedbackRecruiterPage })),
);

const JobBoards = lazy(() => import('views/LoggedIn').then((module) => ({ default: module.JobBoards })));
const JobBoardForm = lazy(() => import('views/LoggedIn').then((module) => ({ default: module.JobBoardForm })));
const ATSIntegrations = lazy(() => import('views/LoggedIn').then((module) => ({ default: module.ATSIntegrations })));
const ATSIntegrationForm = lazy(() =>
  import('views/LoggedIn').then((module) => ({ default: module.ATSIntegrationForm })),
);
const UserMe = lazy(() => import('views/LoggedIn').then((module) => ({ default: module.UserMe })));
const UserProfile = lazy(() => import('views/LoggedIn').then((module) => ({ default: module.UserProfile })));
const BrowserExtension = lazy(() => import('views/LoggedIn').then((module) => ({ default: module.BrowserExtension })));
const Teams = lazy(() => import('views/LoggedIn').then((module) => ({ default: module.Teams })));
const TeamForm = lazy(() => import('views/LoggedIn').then((module) => ({ default: module.TeamForm })));
const TenantEmailSettingsForm = lazy(() =>
  import('views/LoggedIn').then((module) => ({ default: module.TenantEmailSettingsForm })),
);
const TeamDetails = lazy(() => import('views/LoggedIn').then((module) => ({ default: module.TeamDetails })));
const Users = lazy(() => import('views/LoggedIn').then((module) => ({ default: module.Users })));
const Notifications = lazy(() => import('views/LoggedIn').then((module) => ({ default: module.Notifications })));

/**
 * V2
 */
const SharedCandidatePage = lazy(() => import('pages/SharedCandidatePage/SharedCandidatePage'));
const RobinWrappedPage = lazy(() => import('pages/RobinWrappedPage/RobinWrappedPage'));
const GoogleTranslateNotAvailablePage = lazy(
  () => import('pages/GoogleTranslateNotAvailablePage/GoogleTranslateNotAvailablePage'),
);
const DashboardPage = lazy(() => import('pages/DashboardPage/DashboardPage'));
const EmailSettingsPage = lazy(() =>
  import('pages/EmailSettingsPage/EmailSettingsPage').then((module) => ({ default: module.EmailSettingsWithProvider })),
);
const QuestionnairePage = lazy(() =>
  import('views/LoggedOut/RJCommCandidateQuestionnaire/QuestionnairePage').then((module) => ({
    default: module.QuestionnairePage,
  })),
);
const CandidateProfilePage = lazy(() =>
  import('views/LoggedOut/RJCommCandidateProfile/CandidateProfilePage').then((module) => ({
    default: module.CandidateProfilePage,
  })),
);

/**
 * V3
 */
const SearchV3MainLayout = lazy(() =>
  import('pages/SearchV3/SearchV3MainLayout').then((module) => ({ default: module.SearchV3MainLayout })),
);
const SearchV3List = lazy(() =>
  import('pages/SearchV3/SearchV3List').then((module) => ({ default: module.SearchV3List })),
);
const SearchV3 = lazy(() => import('pages/SearchV3/SearchV3').then((module) => ({ default: module.SearchV3 })));
const LandingPage = lazy(() =>
  import('pages/LandingPage/LandingPage').then((module) => ({ default: module.LandingPage })),
);
const RatedCandidatesPage = lazy(() =>
  import('pages/RatedCandidatesPage/RatedCandidatesPage').then((module) => ({ default: module.RatedCandidatesPage })),
);
const CentralInboxPage = lazy(() =>
  import('pages/CentralInboxPage/CentralInboxPage').then((module) => ({ default: module.CentralInboxPage })),
);

const PublicLayout = lazy(() =>
  import('shared/legacy/components/layouts/PublicLayout/PublicLayout').then((module) => ({
    default: module.PublicLayout,
  })),
);
const AdminLayout = lazy(() => import('shared/legacy/components/layouts/AdminLayout/AdminLayout'));
const Dashboard = lazy(() => import('views/Admin/routes/Dashboard/Dashboard'));
const OAuthNylasFailPopup = lazy(() => import('pages/EmailSettingsPage/components/OAuthNylasFailPopup'));
const OAuthNylasSuccessPopup = lazy(() => import('pages/EmailSettingsPage/components/OAuthNylasSuccessPopup'));

const Routes = () => {
  useRedirectWhenTranslateOn();
  useBrowserExtensionSync();
  useRefreshToken();
  useGetFeatureTogglesQuery();
  useGetTenantBrandingQuery();

  return (
    <>
      <ReactRouterDomRoutes>
        <Route element={<JamParams />}>
          {[
            routes.login,
            /** this route doesn't actually exist but is sent in emails, we need to remove it from emails so we can remove it from here */
            '/Account/EmailConfirmation',
          ].map((path, index) => (
            <Route
              key={index}
              path={path}
              element={
                <RoutesSuspense>
                  <LogIn />
                </RoutesSuspense>
              }
            />
          ))}
          <Route
            path={routes.loginTenant}
            element={
              <RoutesSuspense>
                <LoginTenant />
              </RoutesSuspense>
            }
          />
          <Route
            path={routes.recoverPassword}
            element={
              <RoutesSuspense>
                <PasswordRecovery />
              </RoutesSuspense>
            }
          />
          <Route
            path={routes.resetPassword}
            element={
              <RoutesSuspense>
                <ResetPassword />
              </RoutesSuspense>
            }
          />
          <Route
            path={routes.multiFactorAuth}
            element={
              <RoutesSuspense>
                <MultiFactorAuth />
              </RoutesSuspense>
            }
          />
          <Route
            path={routes.gtNotAvailable}
            element={
              <RoutesSuspense>
                <GoogleTranslateNotAvailablePage />
              </RoutesSuspense>
            }
          />
          <Route
            path={routes.nylasSuccess}
            element={
              <RoutesSuspense>
                <OAuthNylasSuccessPopup />
              </RoutesSuspense>
            }
          />
          <Route
            path={routes.nylasFailed}
            element={
              <RoutesSuspense>
                <OAuthNylasFailPopup />
              </RoutesSuspense>
            }
          />
          <Route
            element={
              <RoutesSuspense>
                <PublicLayout />
              </RoutesSuspense>
            }>
            <Route
              path={routes.rjCommHubPublicVacancyDetails}
              element={
                <RoutesSuspense>
                  <RJCommHubPublicVacancyDetails />
                </RoutesSuspense>
              }
            />
            <Route
              path={routes.rjCommHubCandidateInterested}
              element={
                <RoutesSuspense>
                  <RJCommHubCandidateInterested />
                </RoutesSuspense>
              }
            />
            <Route
              path={routes.rjCommHubCandidateNotInterested}
              element={
                <RoutesSuspense>
                  <JobRecommendationsPage />
                </RoutesSuspense>
              }
            />
            <Route
              path={routes.rjCommHubCandidateConfirmation()}
              element={
                <RoutesSuspense>
                  <RJCommHubCandidateConfirmation />
                </RoutesSuspense>
              }
            />
            <Route
              path={routes.rjCommHubQuestionnaireConfirmation}
              element={
                <RoutesSuspense>
                  <RJCommHubQuestionnaireConfirmation />
                </RoutesSuspense>
              }
            />
            <Route
              path={routes.rjCommHubJobAlertsConfirmation}
              element={
                <RoutesSuspense>
                  <RJCommHubJobAlertsConfirmation />
                </RoutesSuspense>
              }
            />
            <Route
              path={routes.optOutCommunication}
              element={
                <RoutesSuspense>
                  <OptOutCommunication />
                </RoutesSuspense>
              }
            />
            <Route
              path={routes.interviewFeedbackCandidate}
              element={
                <RoutesSuspense>
                  <InterviewFeedbackCandidatePage />
                </RoutesSuspense>
              }
            />
            <Route
              path={routes.questionnaire}
              element={
                <RoutesSuspense>
                  <QuestionnairePage />
                </RoutesSuspense>
              }
            />
            <Route
              path={routes.jobRecommendationDetails}
              element={
                <RoutesSuspense>
                  <JobRecommendationDetailsPage />
                </RoutesSuspense>
              }
            />
            <Route
              path={routes.jobRecommendationNotificationDetail}
              element={
                <RoutesSuspense>
                  <JobRecommendationNotificationDetailPage />
                </RoutesSuspense>
              }
            />
            <Route
              path={routes.jobRecommendationNotificationList}
              element={
                <RoutesSuspense>
                  <JobRecommendationsPage />
                </RoutesSuspense>
              }
            />
            <Route
              path={routes.jobNotificationsEnabled}
              element={
                <RoutesSuspense>
                  <JobNotificationsEnabledPage />
                </RoutesSuspense>
              }
            />
            <Route
              path={routes.jobRecommendationsPaused}
              element={
                <RoutesSuspense>
                  <JobRecommendationsPausedPage />
                </RoutesSuspense>
              }
            />
            <Route
              path={routes.candidateProfile}
              element={
                <RoutesSuspense>
                  <CandidateProfilePage />
                </RoutesSuspense>
              }
            />
          </Route>
          <Route
            path={routes.admin.root}
            element={
              <RoutesSuspense>
                <RequireAuth
                  shouldAllow={({ auth }) => ({ isAllowed: auth.isStaff, redirectTo: routes.admin.dashboard })}>
                  <AdminLayout />
                </RequireAuth>
              </RoutesSuspense>
            }>
            <Route
              path={routes.admin.dashboard}
              element={
                <RoutesSuspense>
                  <Dashboard />
                </RoutesSuspense>
              }
            />
          </Route>
          <Route
            path="/"
            element={
              <RoutesSuspense>
                <RequireAuth>
                  <SearchV3MainLayout />
                </RequireAuth>
              </RoutesSuspense>
            }>
            <Route
              path={routes.home}
              element={
                <RoutesSuspense>
                  <RequireAuth>
                    <LandingPage />
                  </RequireAuth>
                </RoutesSuspense>
              }
            />
            <Route
              path={routes.search({ campaignId: 'campaigns' })}
              element={
                <RoutesSuspense>
                  <RequireAuth>
                    <SearchV3List />
                  </RequireAuth>
                </RoutesSuspense>
              }
            />
            (
            {[
              routes.search(),
              routes.search({ campaignId: ':id' }),
              routes.search({ campaignId: ':id', tab: ':tab' }),
            ].map((route, index) => (
              <Route
                key={index}
                path={route}
                element={
                  <RoutesSuspense>
                    <SearchV3 />
                  </RoutesSuspense>
                }
              />
            ))}
            )
            <Route
              path={routes.jobBoards}
              element={
                <RoutesSuspense>
                  <JobBoards />
                </RoutesSuspense>
              }
            />
            <Route
              path={routes.newJobBoard}
              element={
                <RoutesSuspense>
                  <JobBoardForm />
                </RoutesSuspense>
              }
            />
            <Route
              path={routes.jobBoard()}
              element={
                <RoutesSuspense>
                  <JobBoardForm />
                </RoutesSuspense>
              }
            />
            <Route
              path={routes.atsConnections}
              element={
                <RoutesSuspense>
                  <RequireAuth
                    shouldAllow={({ auth }) => ({
                      isAllowed: auth.isAdmin,
                      redirectTo: routes.home,
                    })}>
                    <ATSIntegrations />
                  </RequireAuth>
                </RoutesSuspense>
              }
            />
            <Route
              path={routes.newAtsConnection}
              element={
                <RoutesSuspense>
                  <RequireAuth
                    shouldAllow={({ auth }) => ({
                      isAllowed: auth.isAdmin,
                      redirectTo: routes.home,
                    })}>
                    <ATSIntegrationForm />
                  </RequireAuth>
                </RoutesSuspense>
              }
            />
            <Route
              path={routes.atsConnection()}
              element={
                <RoutesSuspense>
                  <RequireAuth
                    shouldAllow={({ auth }) => ({
                      isAllowed: auth.isAdmin,
                      redirectTo: routes.home,
                    })}>
                    <ATSIntegrationForm />
                  </RequireAuth>
                </RoutesSuspense>
              }
            />
            <Route
              path={routes.userMe}
              element={
                <RoutesSuspense>
                  <UserMe />
                </RoutesSuspense>
              }
            />
            <Route
              path={routes.users}
              element={
                <RoutesSuspense>
                  <RequireAuth
                    shouldAllow={({ auth }) => ({
                      isAllowed: auth.isAdmin,
                      redirectTo: routes.home,
                    })}>
                    <Users />
                  </RequireAuth>
                </RoutesSuspense>
              }
            />
            <Route
              path={routes.newUser}
              element={
                <RoutesSuspense>
                  <RequireAuth
                    shouldAllow={({ auth }) => ({
                      isAllowed: auth.isAdmin,
                      redirectTo: routes.home,
                    })}>
                    <UserProfile />
                  </RequireAuth>
                </RoutesSuspense>
              }
            />
            <Route
              path={routes.user()}
              element={
                <RoutesSuspense>
                  <RequireAuth
                    shouldAllow={({ auth }) => ({
                      isAllowed: auth.isAdmin,
                      redirectTo: routes.home,
                    })}>
                    <UserProfile />
                  </RequireAuth>
                </RoutesSuspense>
              }
            />
            <Route
              path={routes.browserExtension}
              element={
                <RoutesSuspense>
                  <BrowserExtension />
                </RoutesSuspense>
              }
            />
            <Route
              path={routes.notifications}
              element={
                <RoutesSuspense>
                  <Notifications />
                </RoutesSuspense>
              }
            />
            <Route
              path={routes.teams}
              element={
                <RoutesSuspense>
                  <RequireAuth
                    shouldAllow={({ auth }) => ({
                      isAllowed: auth.isAdmin,
                      redirectTo: routes.home,
                    })}>
                    <Teams />
                  </RequireAuth>
                </RoutesSuspense>
              }
            />
            <Route
              path={routes.newTeam}
              element={
                <RoutesSuspense>
                  <RequireAuth
                    shouldAllow={({ auth }) => ({
                      isAllowed: auth.isAdmin,
                      redirectTo: routes.home,
                    })}>
                    <TeamForm />
                  </RequireAuth>
                </RoutesSuspense>
              }
            />
            <Route
              path={routes.tenantEmailSettings}
              element={
                <RoutesSuspense>
                  <TenantEmailSettingsForm />
                </RoutesSuspense>
              }
            />
            <Route
              path={routes.team()}
              element={
                <RoutesSuspense>
                  <RequireAuth
                    shouldAllow={({ auth }) => ({
                      isAllowed: auth.isAdmin,
                      redirectTo: routes.home,
                    })}>
                    <TeamForm />
                  </RequireAuth>
                </RoutesSuspense>
              }
            />
            <Route
              path={routes.detailsTeam()}
              element={
                <RoutesSuspense>
                  <RequireAuth
                    shouldAllow={({ auth }) => ({
                      isAllowed: auth.isAdmin,
                      redirectTo: routes.home,
                    })}>
                    <TeamDetails />
                  </RequireAuth>
                </RoutesSuspense>
              }
            />
            <Route
              path={routes.sharedCandidatePage()}
              element={
                <RoutesSuspense>
                  <SharedCandidatePage />
                </RoutesSuspense>
              }
            />
            <Route
              path={routes.candidatePage()}
              element={
                <RoutesSuspense>
                  <SharedCandidatePage />
                </RoutesSuspense>
              }
            />
            <Route
              path={routes.emailSettings}
              element={
                <RoutesSuspense>
                  <EmailSettingsPage />
                </RoutesSuspense>
              }
            />
            <Route
              path={routes.dashboard}
              element={
                <RoutesSuspense>
                  <DashboardPage />
                </RoutesSuspense>
              }
            />
            {shouldShowRobinWrapped() && (
              <Route
                path={routes.robinWrapped}
                element={
                  <RoutesSuspense>
                    <RobinWrappedPage />
                  </RoutesSuspense>
                }
              />
            )}
            <Route
              path={routes.ratedCandidates}
              element={
                <RoutesSuspense>
                  <RatedCandidatesPage />
                </RoutesSuspense>
              }
            />
            <Route
              path={routes.centralInbox}
              element={
                <RoutesSuspense>
                  <CentralInboxPage />
                </RoutesSuspense>
              }
            />
            <Route
              path={routes.interviewFeedbackRecruiter}
              element={
                <RoutesSuspense>
                  <InterviewFeedbackRecruiterPage />
                </RoutesSuspense>
              }
            />
          </Route>
          <Route path={routes.robinJr.home} element={<RobinJrRouteGuard />}>
            <Route element={<RobinJrLayout />}>
              <Route index element={<RobinJrHomeLazy />} />
              <Route path={routes.robinJr.chat()} element={<RobinJrChatLazy />} />
            </Route>
          </Route>

          <Route path={routes.oauth2} />
          <Route path="*" element={<Navigate to={routes.notFound} />} />
        </Route>
      </ReactRouterDomRoutes>

      <SharedLayoutComponents />
    </>
  );
};

export default Routes;
